// Import react elements
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

// Import navigation hooks from React Router
import { useLocation, useNavigate } from "react-router-dom";

// Import context functions
import { AuthContext } from "global/context/AuthContext";
import { UserContext } from "global/context/UserContext";

// Import strich api calls
import {
    deletePodDesign,
    getPrintAreaRoles,
    updatePodDesignDetails
} from "api/APIStrichCalls";

// Import hooks
import usePageTitle from "global/hooks/usePageTitle";
import { useToast } from "global/hooks/useToast";
import usePodProductData from "./hooks/usePodDesignData";

// Import components
import { Grid, GridItem } from "global/components/Grid";
import { LoadingSpinner } from 'global/components/loading_ui/LoadingSpinnerAnimation';
import CreateButton from "global/components/CreateButton";
import ToggleSwitch from 'global/components/ToggleSwitch';
import  PodDesignsTable  from "./components/PodDesignsTable";
import  PodDesignDetailsPopUp  from "./components/PodDesignDetailsPopUp";

// Import utility functions
import { S2B } from "global/utilities/BooleanConverters";
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";

// Import css stylesheet
import "./PodDesignsPage.css";





// PodProductsPage.js
const PodDesignsPage = ({designOrTemplate = "design"}) => {


    const pageTitle = designOrTemplate === "design" ? "Print on Demand Designs" : "Print on Demand Templates"

    // Set title inside the page
    usePageTitle(pageTitle);

    // Get the navigate function for programmatic navigation
    const navigate = useNavigate();

    // Get the current route so incoming POD design data can be read
    const location = useLocation();

    // Store view grid type (grid or table) - true = grid, flase = table
    const [viewGridType, setViewGridType] = useState(true);


    // Custom hook for fetching and managing pod design and product data
    const {
        fetchPodDesigns,
        loadingPodDesigns,
        podDesignsData,
        setPodDesignsData,
        existingCategories,
    } = usePodProductData();

    
    //console.log("podDesignsData: ", podDesignsData);

    // Get the token from AuthContext
    const { token } = useContext(AuthContext);

    // Define toast options
    const { toastError } = useToast();

    // Get user connection data
    const { connectionsData } = useContext(UserContext);

    // Set state for selected store filter
    const [selectedStoreFilter, setSelectedStoreFilter] = useState("all");

    // Set state for whether inactive designs should be shown
    const [showInactiveDesigns, setShowInactiveDesigns] = useState(true);

    // Set state for whether strich templates should be shown
    const [showStrichTemplates, setShowStrichTemplates] = useState(true);

    // Set state for design search filter
    const [designSearchValue, setDesignSearchValue] = useState("");

    // Set state for all roles data
    const [allRoles, setAllRoles] = useState({});

    // Set error message state for all roles data
    const [allRolesErrorMessage, setAllRolesErrorMessage] = useState(null);

    // Set state for selected design or template id
    const [selectedDesignOrTemplateId, setSelectedDesignOrTemplateId] = useState(null);

    // Set state for design or template pop up
    const [podDesignOrTemplatePopupOpen, setPodDesignOrTemplatePopupOpen] = useState(null);

    // Store initial data passed in when creating a design from another portal page
    const [newDesignPrefill, setNewDesignPrefill] = useState(null);

    // Remember the document position before opening a design or template
    const podDesignsScrollPositionRef = useRef(0);

    // Keep restoring that position while the editor can refresh the underlying list
    const restorePodDesignsScrollPositionRef = useRef(false);


    // Open a new design automatically when another portal page supplied an image
    useEffect(() => {

        // Read the incoming POD design data
        const incomingDesign = location.state?.createPodDesign;

        // Only handle this shortcut on the normal designs page
        if (designOrTemplate !== "design" || !incomingDesign?.image_url) {

            return;

        }

        // Store the supplied image and title for the design editor
        setNewDesignPrefill(incomingDesign);

        // Open the normal new-design editor
        setSelectedDesignOrTemplateId("addnew");
        setPodDesignOrTemplatePopupOpen(true);

        // Remove the transient navigation state so refreshing does not create it again
        navigate(location.pathname, {

            replace: true,
            state: null,

        });

    }, [designOrTemplate, location.pathname, location.state, navigate]);


    // Remember where the user currently is in the designs/templates list
    const rememberPodDesignsScrollPosition = () => {

        // Start with the browser window position
        let currentScrollPosition = window.scrollY;

        // Prefer the document scrolling element when available
        if (document.scrollingElement) {
            currentScrollPosition = document.scrollingElement.scrollTop;
        }

        // Store the current vertical position
        podDesignsScrollPositionRef.current = currentScrollPosition;

        // Keep restoring it while the editor is open and refreshing the list underneath
        restorePodDesignsScrollPositionRef.current = true;

    };


    // Restore the designs/templates list after an editor-triggered refresh
    useEffect(() => {

        // Do nothing unless an editor/list action asked us to preserve the position
        if (!restorePodDesignsScrollPositionRef.current) {
            return;
        }

        // Wait until the refreshed designs/templates have finished loading
        if (loadingPodDesigns) {
            return;
        }

        // Wait until list data is available
        if (!Array.isArray(podDesignsData)) {
            return;
        }

        // Store both animation frame ids for cleanup
        let firstAnimationFrameId = 0;
        let secondAnimationFrameId = 0;

        // Wait one frame for the list to render
        firstAnimationFrameId = window.requestAnimationFrame(() => {

            // Wait another frame for the cards/table layout to settle
            secondAnimationFrameId = window.requestAnimationFrame(() => {

                // Normalise the stored position
                let restoredScrollPosition = Number(podDesignsScrollPositionRef.current);

                // Fall back to the top for an invalid stored position
                if (!Number.isFinite(restoredScrollPosition) || restoredScrollPosition < 0) {
                    restoredScrollPosition = 0;
                }

                // Restore the document position
                if (document.scrollingElement) {

                    document.scrollingElement.scrollTop = restoredScrollPosition;

                } else {

                    window.scrollTo(0, restoredScrollPosition);

                }

                // Keep the position armed while the popup is open because Save can refresh again
                if (!podDesignOrTemplatePopupOpen) {
                    restorePodDesignsScrollPositionRef.current = false;
                }

            });

        });

        // Cancel pending restoration if another render happens first
        return () => {

            window.cancelAnimationFrame(firstAnimationFrameId);
            window.cancelAnimationFrame(secondAnimationFrameId);

        };

    }, [
        loadingPodDesigns,
        podDesignsData,
        podDesignOrTemplatePopupOpen,
    ]);
    

    // Function to get all the roles (so we can use the nice title)
    const getAllRoles = async () => {

        // Make the call
        const allRolesResponse = await getPrintAreaRoles(token);

        if (allRolesResponse.success) {

            // Update the state
            setAllRoles(allRolesResponse.results);

        } else {

            // Set the error message
            setAllRolesErrorMessage(allRolesResponse.error_message);

        }

    };



    // useEffect to fetch all roles
    useEffect(() => {

        // Call get roles 
        getAllRoles();
       
        // Dependencies that trigger a new instance of this function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);



    // Build the list of available store connections for filtering
    const storeFilterOptions = useMemo(() => {

        // Ensure connections data is an array
        const safeConnectionsData = Array.isArray(connectionsData) ? connectionsData : [];

        // Only include Strich stores
        return safeConnectionsData.filter((connection) => connection?.conntype === "STRICH");

    }, [connectionsData]);



    // Apply the selected store, search and active-state filters
    const filteredPodDesignsData = useMemo(() => {

        // Ensure designs data is an array
        const safePodDesignsData = Array.isArray(podDesignsData) ? podDesignsData : [];

        // Normalise the search value
        const normalisedSearchValue = String(designSearchValue || "").trim().toLowerCase();

        // Return filtered designs
        return safePodDesignsData.filter((design) => {

            // Hide inactive designs when the toggle is off
            if (designOrTemplate === "design" && !showInactiveDesigns && design?.active !== "Y") {

                return false;

            }

            // Hide strich templates when the toggle is off
            if (!showStrichTemplates && design?.source === "strich") {

                return false;

            }

            // Filter by typed search value
            if (normalisedSearchValue) {

                // Build searchable design text
                const searchableDesignText = [
                    design?.title,
                    design?.sku,
                    design?.code,
                    design?.id,
                    design?.store_name,
                    design?.connection_name,
                ].filter(Boolean).join(" ").toLowerCase();

                // Hide designs that do not match the search value
                if (!searchableDesignText.includes(normalisedSearchValue)) {

                    return false;

                }

            }

            // If all stores are selected, keep the design
            if (selectedStoreFilter === "all") {

                return true;

            }

            // Keep designs for the selected store only
            return String(design?.store_id || "") === String(selectedStoreFilter);

        });

    }, [podDesignsData, selectedStoreFilter, showInactiveDesigns, showStrichTemplates, designSearchValue, designOrTemplate]);



    // useEffect to automatically fetch pod designs when the component mounts or when dependencies update
    useEffect(() => {

        if (!designOrTemplate || !fetchPodDesigns) { return; }

        // Call fetchPodDesigns on component mount or when token/authReady change
        fetchPodDesigns(designOrTemplate);
       
        // Dependencies that trigger a new instance of this function
    }, [designOrTemplate, fetchPodDesigns]);


  
    // Functiont to handle selecting POD design when clicking 'view' in table menu
    const handleSelectPodDesign = async (podProductID) => {

        // Remember exactly where the user was before opening the editor
        rememberPodDesignsScrollPosition();

        // Set selected id
        setSelectedDesignOrTemplateId(podProductID);

        // Open the pop up
        setPodDesignOrTemplatePopupOpen(true);

    };



    // Delete a design or template directly from its grid card
    const handleDeletePodDesignCard = async (design) => {

        // Stop if there is no valid design/template id
        if (!design?.id) { return; }

        // Strich-provided templates are read-only and cannot be deleted
        if (designOrTemplate === "template" && design.source === "strich") { return; }

        // Use the current page type in the confirmation text
        const itemTypeTitle = designOrTemplate === "template" ? "Template" : "Design";

        // Display the same confirmation used by the table delete action
        DisplayConfirmationDialog(

            `<h3>Are you sure?</h3> <br/> Delete POD ${itemTypeTitle}:<br/><br/> ${design.title} <br/><br/><span class='warning-message'>WARNING: This cannot be undone.</span>`,
            "Delete",
            "Cancel",
            async function (is_confirmed) {

                // Stop if the user cancelled
                if (!is_confirmed) { return; }

                // Preserve the current grid position before refreshing the designs
                rememberPodDesignsScrollPosition();

                // Delete the design/template
                const response = await deletePodDesign(token, designOrTemplate, design.id);

                // Refresh the grid after a successful deletion
                if (response?.success) {

                    fetchPodDesigns(designOrTemplate);
                    return;

                }

                // Surface dependency errors and any other API failure
                toastError({
                    title: "Error",
                    message: response?.error_message || `Could not delete ${designOrTemplate}.`,
                    autoCloseMs: 6000,
                });

            }

        );

    };


    // Toggle products active state to show / hide on the store
    const handleShowHideDesignOnStore = async (podProductID, activeState) => {

        // Create active payload (must include id)
        const active_payload = {
            id: podProductID,
            active: activeState ? "Y" : "N"
        };

        // Make the quick PUT call to update the active state
        const activeResponse = await updatePodDesignDetails(token, designOrTemplate, podProductID, active_payload);

        // Check if it updated successfully
        if (activeResponse.success) {

            // Set store locally for immediate update
            setPodDesignsData((prev) => {

                // Ensure array
                const safePrev = Array.isArray(prev) ? prev : [];

                // Update matching row
                return safePrev.map((row) => {

                    // If not our row, leave unchanged
                    if (String(row?.id) !== String(podProductID)) {

                        // Return unchanged row
                        return row;

                    }

                    // Return updated row
                    return {

                        // Keep everything else
                        ...row,

                        // Update active flag (match your API shape)
                        active: activeState ? "Y" : "N",

                    };

                });

            })

        };

    };
    
   

    // If there is a roles error message then return early and display the message
    if (allRolesErrorMessage) {

        return (

            <Grid>
                            
                <GridItem>

                    <div className="dashboard-status-panel dashboard-status-panel--error">

                        <h3>POD printable roles unavailable</h3>
                        <p>{allRolesErrorMessage}</p>

                    </div>

                </GridItem>

            </Grid>

        );

    }


    return (

        <>

            <Grid>

                <GridItem>

                    <div className="pod-designs-page__header">

                        <div>
                            <h2>{designOrTemplate === "design" ? "Designs" : "Templates"}</h2>
                        </div>

                        <div className='pod-designs-page__header-right'>

                            {/* This is the view mode toggle */}
                            <div className="pod-designs-page__view-toggle pod-pricing-page__section-tax-toggle">
                                <button
                                    type="button"
                                    className={`pod-pricing-page__segmented-button ${!viewGridType ? "pod-pricing-page__segmented-button--active" : ""}`}
                                    onClick={() => setViewGridType(false)}
                                >
                                    Table
                                </button>
                                <button
                                    type="button"
                                    className={`pod-pricing-page__segmented-button ${viewGridType ? "pod-pricing-page__segmented-button--active" : ""}`}
                                    onClick={() => setViewGridType(true)}
                                >
                                    Grid
                                </button>
                            </div>

                        </div>

                    </div>

                    <div className="pod-designs-page__filters-container">

                        <div className="pod-designs-page__filters">

                            <input
                                className="pod-designs-page__search-filter"
                                type="text"
                                value={designSearchValue}
                                placeholder={`Search ${designOrTemplate === "design" ? "designs" : "templates"}`}
                                onChange={(event) => setDesignSearchValue(event.target.value)}
                            />

                            {designOrTemplate === "design" &&

                                <select
                                    className="pod-designs-page__store-filter"
                                    value={selectedStoreFilter}
                                    onChange={(event) => setSelectedStoreFilter(event.target.value)}
                                >
                                    <option value="all">All stores</option>

                                    {storeFilterOptions.map((connection) => (
                                        <option key={connection.id} value={connection.id}>
                                            {connection.name || connection.connectionname || connection.store_name || `Store ${connection.id}`}
                                        </option>
                                    ))}
                                </select>

                            }

                            {designOrTemplate === "design" &&

                                <label className="pod-designs-page__inactive-filter">
                                    <span>Show inactive</span>
                                    <ToggleSwitch
                                        title="Show inactive designs"
                                        size="s"
                                        value={showInactiveDesigns}
                                        onChange={(event) => setShowInactiveDesigns(event.target.value)}
                                    />
                                </label>

                            }

                            {designOrTemplate === "template" &&

                                <label className="pod-designs-page__inactive-filter">
                                    <span>Show Strich Provided Templates</span>
                                    <ToggleSwitch
                                        title="Show Strich templates"
                                        size="s"
                                        value={showStrichTemplates}
                                        onChange={(event) => setShowStrichTemplates(event.target.value)}
                                    />
                                </label>

                            }

                        </div>
                
                    </div>


                    {viewGridType ? 


                        <div>

                            <br/>
                           
                            {loadingPodDesigns ? 
                                
                                <LoadingSpinner message="Loading..."/>
                                                                
                                :

                                <div
                                    id="products"
                                    className="pod-designs"
                                >

                                    {/* Display an Add Design / Add Template card as the first grid item */}
                                    <button
                                        type="button"
                                        className="pod-designs__card pod-designs__add-card"
                                        onClick={() => {

                                            // This is a normal blank design/template rather than one supplied by another page
                                            setNewDesignPrefill(null);

                                            // Open the normal new-design/template editor
                                            setSelectedDesignOrTemplateId("addnew");
                                            setPodDesignOrTemplatePopupOpen(true);

                                        }}
                                    >

                                        <span className="pod-designs__add-card-plus">+</span>

                                        <span className="pod-designs__add-card-title">
                                            {designOrTemplate === "design" ? "Add Design" : "Add Template"}
                                        </span>

                                        <span className="pod-designs__add-card-text">
                                            {designOrTemplate === "design"
                                                ? "Create a new print on demand design"
                                                : "Create a new print on demand template"}
                                        </span>

                                    </button>

                                    {filteredPodDesignsData.map((design) => {

                                        return (

                                            <div
                                                key={design.id}
                                                className={`pod-designs__card ${designOrTemplate === "design" && design.active !== "Y" ? "pod-designs__card--inactive" : ""}`}
                                                onClick={() => {

                                                    // Click function to get product details and generate mockups if it needs to
                                                    handleSelectPodDesign(design.id);

                                                }}
                                            >

                                                <div className="pod-designs__header" onClick={(e) => e.stopPropagation()}>

                                                    <div className="pod-designs__title">
                                                        {design.title}
                                                    </div>

                                                    {/* Designs use this position for store visibility */}
                                                    {designOrTemplate === "design" &&

                                                        <ToggleSwitch 
                                                            title={S2B(design.active) ? "Hide design on store" : "Show design on store"}
                                                            size="xs"
                                                            value={S2B(design.active)} 
                                                            onChange={(e) => {handleShowHideDesignOnStore(design.id, e.target.value);}} 
                                                        />

                                                    }

                                                    {/* Strich templates use the same position for their source marker */}
                                                    {designOrTemplate === "template" && design.source === "strich" &&

                                                        <div className="pod-pricing-page__plan-badge pod-designs__strich-badge">
                                                            Strich
                                                        </div>

                                                    }

                                                </div>

                                                <div
                                                    className={
                                                        design.roles.length > 1
                                                            ? "pod-designs__roles pod-designs__roles--multi"
                                                            : "pod-designs__roles pod-designs__roles--single"
                                                    }
                                                >

                                                    {design.roles.map((role) => {

                                                        const matchingRole = allRoles?.find((r) => r.role === role.role);

                                                        return (

                                                            <div
                                                                key={role.role}
                                                                className="pod-designs__roleCard"
                                                            >

                                                                <div className="pod-designs__imageWrap">

                                                                    <img
                                                                        className="pod-designs__image"
                                                                        src={role.thumbnail_url}
                                                                        alt={matchingRole?.name || role.role}
                                                                    />

                                                                </div>

                                                                {design.numberOfRoles > 1 ? 

                                                                    <span className="pod-designs__roleTitle">
                                                                        {matchingRole?.name || ""}
                                                                    </span>

                                                                    : ""

                                                                }

                                                            </div>

                                                        );

                                                    })}

                                                </div>

                                                {/* Keep direct card actions and template information in a dedicated footer */}
                                                <div className="pod-designs__card-footer">

                                                    {/* Show the assigned template name on design cards */}
                                                    {designOrTemplate === "design" && design.templateName &&

                                                        <div
                                                            className="pod-designs__template-name"
                                                            title={"Template: " + design.templateName}
                                                        >
                                                            {design.templateName}
                                                        </div>

                                                    }

                                                    {/* User designs and templates can be deleted; Strich templates cannot */}
                                                    {!(designOrTemplate === "template" && design.source === "strich") &&

                                                        <CreateButton
                                                            icon="/images/icon_bin.png"
                                                            iconTitle="Delete"
                                                            tooltip={`Delete ${designOrTemplate === "template" ? "Template" : "Design"}`}
                                                            size="xs"
                                                            isDelete={true}
                                                            onClick={() => handleDeletePodDesignCard(design)}
                                                        />

                                                    }

                                                </div>

                                            </div>

                                        );

                                    })}

                                </div>
                            }        

                        </div>
                        
                    :
                        <>

                            <br/>

                            <PodDesignsTable
                                designOrTemplate={designOrTemplate}
                                podDesignsData={filteredPodDesignsData}
                                handleSelectPodDesign={handleSelectPodDesign}
                                fetchPodDesigns={fetchPodDesigns}
                                loadingPodDesigns={loadingPodDesigns}
                                handleShowHideDesignOnStore={handleShowHideDesignOnStore}
                                rememberPodDesignsScrollPosition={rememberPodDesignsScrollPosition}
                                onAdd={() => {

                                    // This is a normal blank design/template rather than one supplied by another page
                                    setNewDesignPrefill(null);

                                    // Open the normal new-design/template editor
                                    setSelectedDesignOrTemplateId("addnew");
                                    setPodDesignOrTemplatePopupOpen(true);

                                }}
                            />
                        
                        </>
                    }   
                   
                </GridItem>

            </Grid>


            {podDesignOrTemplatePopupOpen &&
            
                <PodDesignDetailsPopUp
                    onClose={() => {

                        // Close the pop up
                        setPodDesignOrTemplatePopupOpen(false);

                        // Clear the selected id
                        setSelectedDesignOrTemplateId(null);

                        // Clear any one-off image/title supplied by another page
                        setNewDesignPrefill(null);

                    }} 
                    designOrTemplate={designOrTemplate} 
                    designOrTemplateId={selectedDesignOrTemplateId}
                    setSelectedDesignOrTemplateId={setSelectedDesignOrTemplateId}
                    fetchPodDesigns={fetchPodDesigns}
                    existingCategories={existingCategories}
                    initialDesignData={newDesignPrefill}
                />
            
            }


        </>

    );

};

export default PodDesignsPage;